import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { BodyOf } from "../../../../../core/api";
import useApi from "../../../../../shared/hooks/useApi";
import useEntityProfileToast from "../../../../entityProfileModal/hooks/useEntityProfileToast";
import type { CaregiverProfileCaregiver } from "../../../caregiverProfileModal.types";

type CaregiverSuppliesSizesBody = Partial<
  BodyOf<"put", "/agencies/:agencyId/caregivers/:caregiverId/supplies_sizes">
>;

export function useCaregiverSuppliesShipments(caregiverId: CaregiverProfileCaregiver["id"]) {
  const { api } = useApi();

  return useQuery({
    queryKey: ["caregiverProfileModal", caregiverId, "suppliesShipments"],
    queryFn: () =>
      api.get("./caregiver_supplies_shipments/:caregiverId", {
        path: { caregiverId },
      }),
  });
}

export function useUpdateCaregiverSupplySizes(caregiverId: CaregiverProfileCaregiver["id"]) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const toast = useEntityProfileToast();

  return useMutation({
    mutationFn: (body: CaregiverSuppliesSizesBody) =>
      api.put("/agencies/:agencyId/caregivers/:caregiverId/supplies_sizes", {
        path: { caregiverId },
        body,
      }),
    onSuccess: (_, body) => {
      queryClient.setQueryData(["caregiver", "get", caregiverId], (currentCaregiver: unknown) => {
        if (
          currentCaregiver === undefined ||
          currentCaregiver === null ||
          typeof currentCaregiver !== "object"
        ) {
          return currentCaregiver;
        }

        return {
          ...currentCaregiver,
          ...body,
        };
      });
      toast({ description: "Caregiver supply sizes updated successfully.", status: "success" });
    },
    onError: () => {
      toast({ description: "Could not update caregiver supply sizes.", status: "error" });
    },
  });
}
